import {
  capitalizeTerm,
  formatAmountWithUnit,
  SELL_MAX_ITEMS,
  type StorefrontTerminology,
} from "@loot-os/api-contract";
import type { InventoryItem, InventoryItemStatus } from "@loot-os/client";
import { BoxImage } from "@loot-os/ui/components/box-image";
import { Button } from "@loot-os/ui/components/button";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@loot-os/ui/components/tabs";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { ErrorBanner } from "../components/ErrorBanner.js";
import { RequireAuth } from "../components/RequireAuth.js";
import { SellBackDialog } from "../components/SellBackDialog.js";
import { useCurrency } from "../lib/currency.js";
import { isSellable, sellBackCents, withdrawalIssueText } from "../lib/inventory.js";
import { inventoryInfiniteQueryOptions } from "../lib/queries.js";
import { useShopper } from "../lib/shopper.js";
import { useTerminology } from "../lib/terminology.js";

/** The tabs, in the order a shopper cares about them: what they hold, what is on the way, what is gone. */
const TABS: {
  status: InventoryItemStatus;
  label: string;
  empty: (terms: StorefrontTerminology) => string;
}[] = [
  {
    status: "owned",
    label: "Owned",
    empty: (t) => `Nothing here yet — open some ${t.box.plural} and claim what you win.`,
  },
  {
    status: "fulfillment_requested",
    label: "Shipping",
    empty: () => "Nothing is being shipped to you right now.",
  },
  { status: "sold", label: "Sold back", empty: () => "You haven't sold anything back yet." },
];

export function InventoryPage() {
  return (
    <RequireAuth>
      <InventoryContent />
    </RequireAuth>
  );
}

/**
 * GET /v1/me/inventory and POST /v1/me/inventory/sell — the units the shopper owns after a
 * claim, and selling them back to the house for their value. Withdrawing one is the tenant's
 * own server's call (`POST /v1/fulfillment/orders`), so there is no withdraw button here.
 */
function InventoryContent() {
  const { sessionToken } = useShopper();
  const currency = useCurrency();
  const terms = useTerminology();
  const [status, setStatus] = useState<InventoryItemStatus>("owned");
  const [selected, setSelected] = useState<string[]>([]);
  const [confirming, setConfirming] = useState(false);

  const query = useInfiniteQuery({
    ...inventoryInfiniteQueryOptions(status),
    enabled: !!sessionToken,
  });
  const items = useMemo(() => query.data?.pages.flatMap((page) => page.data) ?? [], [query.data]);
  const loading = query.isPending || query.isFetchingNextPage;
  const loadError = query.error
    ? query.error instanceof Error
      ? query.error.message
      : `failed to load your ${terms.inventory.singular}`
    : null;

  const sellable = useMemo(() => items.filter(isSellable), [items]);
  // A sale, a refetch or a tab change can take a unit off the page; the selection follows what
  // is actually shown rather than keeping ids the shopper can no longer see.
  const picked = useMemo(
    () => sellable.filter((item) => selected.includes(item.id)),
    [sellable, selected],
  );
  const pickedCents = sellBackCents(picked);
  // One sale carries at most SELL_MAX_ITEMS units, so the selection stops there instead of
  // building one the route would refuse whole.
  const selectionLimit = Math.min(sellable.length, SELL_MAX_ITEMS);
  const allPicked = selectionLimit > 0 && picked.length === selectionLimit;

  const tab = TABS.find((entry) => entry.status === status) ?? TABS[0];

  const toggle = (id: string) =>
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= SELL_MAX_ITEMS) return prev;
      return [...prev, id];
    });

  const switchTab = (next: InventoryItemStatus) => {
    setStatus(next);
    setSelected([]);
  };

  return (
    <div className="grid gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {capitalizeTerm(terms.inventory.singular)}
        </h1>
        <p className="text-sm text-muted-foreground">
          Everything you've claimed. Sell {terms.item.plural} back to the house for their value, any
          time.
        </p>
      </div>

      <Tabs value={status} onValueChange={(value) => switchTab(value as InventoryItemStatus)}>
        <TabsList>
          {TABS.map((entry) => (
            <TabsTrigger key={entry.status} value={entry.status}>
              {entry.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loadError && <ErrorBanner>{loadError}</ErrorBanner>}

      {query.isPending ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {["a", "b", "c", "d"].map((id) => (
            <Skeleton key={id} className="aspect-4/5 rounded-xl" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {tab?.empty(terms)}{" "}
          {status === "owned" && (
            <Link className="underline" to="/boxes">
              Browse {terms.box.plural}
            </Link>
          )}
        </p>
      ) : (
        <>
          {status === "owned" && sellable.length > 0 && (
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="text-muted-foreground">
                {sellable.length} of {items.length} can be sold back
                {sellable.length > SELL_MAX_ITEMS ? `, ${SELL_MAX_ITEMS} at a time` : ""}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  setSelected(
                    allPicked ? [] : sellable.slice(0, SELL_MAX_ITEMS).map((item) => item.id),
                  )
                }
              >
                {allPicked ? "Clear selection" : "Select all"}
              </Button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {items.map((item) => (
              <ItemTile
                key={item.id}
                item={item}
                selected={selected.includes(item.id)}
                onToggle={() => toggle(item.id)}
              />
            ))}
          </div>

          {query.hasNextPage && (
            <Button
              variant="secondary"
              disabled={loading}
              onClick={() => void query.fetchNextPage()}
            >
              {loading ? "Loading…" : "Load more"}
            </Button>
          )}
        </>
      )}

      {/* The action bar sits above the page rather than in it, so a long grid never scrolls it away. */}
      {picked.length > 0 && (
        <div className="sticky bottom-4 z-10 mx-auto flex w-full max-w-lg flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-background/95 px-4 py-3 shadow-lg backdrop-blur">
          <span className="text-sm">
            <span className="font-medium">
              {picked.length} {picked.length === 1 ? terms.item.singular : terms.item.plural}
            </span>{" "}
            <span className="text-muted-foreground">selected</span>
          </span>
          <Button onClick={() => setConfirming(true)}>
            Sell back · {formatAmountWithUnit(pickedCents, currency)}
          </Button>
        </div>
      )}

      {confirming && (
        <SellBackDialog
          open={confirming}
          onOpenChange={setConfirming}
          items={picked}
          onSold={() => setSelected([])}
        />
      )}
    </div>
  );
}

/**
 * One unit. A sellable unit is a button that toggles its selection; anything else is static,
 * because there is nothing to do with a unit already sold or already shipping.
 */
function ItemTile({
  item,
  selected,
  onToggle,
}: {
  item: InventoryItem;
  selected: boolean;
  onToggle: () => void;
}) {
  const currency = useCurrency();
  const terms = useTerminology();
  const sellable = isSellable(item);
  const issueText = withdrawalIssueText(item, terms);
  const note =
    item.status === "sold"
      ? "Sold back"
      : item.status === "fulfillment_requested"
        ? "Shipping to you"
        : item.status === "withdrawn"
          ? "Shipped"
          : item.item.value > 0
            ? formatAmountWithUnit(item.item.value, currency)
            : "Withdraw only";

  const body = (
    <>
      <BoxImage
        src={item.item.imageUrl}
        className={`aspect-square w-full rounded-lg object-cover ${
          item.status === "sold" ? "opacity-50" : ""
        }`}
      />
      <span className="grid gap-0.5 text-left">
        <span className="truncate text-sm font-medium" title={item.item.name}>
          {item.item.name}
        </span>
        <span className="font-mono text-xs text-muted-foreground">{note}</span>
        {issueText && (
          <span className="text-xs text-amber-600 dark:text-amber-500">{issueText}</span>
        )}
      </span>
    </>
  );

  if (!sellable) {
    return <div className="grid gap-2 rounded-xl border border-border bg-card p-3">{body}</div>;
  }

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onToggle}
      className={`grid gap-2 rounded-xl border bg-card p-3 text-left transition-colors ${
        selected ? "border-primary ring-2 ring-primary/40" : "border-border hover:border-primary/50"
      }`}
    >
      {body}
    </button>
  );
}
